import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer
} from 'recharts';
import { ArrowLeft, ExternalLink, TrendingUp, TrendingDown, Users, Video, Globe, DollarSign } from 'lucide-react';
import { getCategories, getChannels, getTrends, getCompetitors, getRealTrend } from '../services/api';
import Tooltip from '../components/Tooltip';
import DataBadge from '../components/DataBadge';
import AiChat from '../components/AiChat';
import UZBEK_NAMES from '../constants/uzbekNames';

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
};

function fmt(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1e9) return (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(n);
}
function fmtRevenue(n) {
  return '$' + (n || 0).toLocaleString('en-US');
}

const TREND_KEYS = {
  "Ko'rishlar": 'views',
  'Obunachilar': 'subscribers',
  'Daromad': 'revenueEstimate',
};

const ChartTooltip = ({ active, payload, label, color }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div style={{
      background: 'rgba(10,14,26,0.92)', border: `1px solid ${color}44`,
      borderRadius: 12, padding: '10px 16px', backdropFilter: 'blur(12px)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.35)', color: '#f1f5f9', fontSize: 13,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label} 2024</div>
      <div>{d.name}: <span style={{ color, fontWeight: 700 }}>{fmt(d.value)}</span></div>
      <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>+12% o'sish</div>
    </div>
  );
};

function darken(hex) {
  if (!hex) return '#000';
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, (n >> 16) - 55);
  const g = Math.max(0, ((n >> 8) & 0xff) - 55);
  const b = Math.max(0, (n & 0xff) - 55);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

const AVATAR_COLORS = ['#50C878', '#6fd99a', '#06b6d4', '#50C878', '#f59e0b'];

function Avatar({ url, name, color, size }) {
  const [err, setErr] = useState(false);
  const letter = (name || '?').charAt(0).toUpperCase();
  const fallbackColor = color || AVATAR_COLORS[(name?.length || 0) % AVATAR_COLORS.length];
  const sz = size || undefined;

  if (!err && url) {
    return (
      <img
        src={url} alt={name}
        className={sz ? undefined : 'ch-avatar'}
        style={sz ? { width: sz, height: sz, borderRadius: '50%', objectFit: 'cover' } : undefined}
        onError={() => setErr(true)}
      />
    );
  }
  return (
    <div
      className={sz ? undefined : 'ch-avatar-fallback'}
      style={{
        ...(sz ? { width: sz, height: sz } : {}),
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${fallbackColor}, ${fallbackColor}88)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: sz ? sz * 0.4 : 13, fontWeight: 700, color: '#fff', flexShrink: 0,
      }}
    >{letter}</div>
  );
}

export default function CategoryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cat, setCat] = useState(null);
  const [channels, setChannels] = useState([]);
  const [trends, setTrends] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTrend, setActiveTrend] = useState("Ko'rishlar");
  const [sortCol, setSortCol] = useState('subscriberCount');
  const [sortAsc, setSortAsc] = useState(false);
  const [realTrend, setRealTrend] = useState({ dataPoints: 0, isRealData: false, history: [] });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getCategories(), getChannels(id), getTrends(id), getCompetitors(id), getRealTrend(id)])
      .then(([cats, chs, tr, comp, rt]) => {
        setCat(cats.find(c => String(c.id) === String(id)) || cats[0]);
        setChannels(chs);
        setTrends(tr);
        setCompetitors(comp);
        setRealTrend(rt || { dataPoints: 0, isRealData: false, history: [] });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);

  if (loading) return <SkeletonDetail />;
  if (!cat) return <div className="page"><p>Kategoriya topilmadi.</p></div>;

  const color = cat.color || '#378ADD';
  const grad = `linear-gradient(135deg, ${color}, ${darken(color)})`;
  const displayName = UZBEK_NAMES[cat.name] || cat.name;

  const sorted = [...channels].sort((a, b) => {
    const av = a[sortCol] ?? 0, bv = b[sortCol] ?? 0;
    return sortAsc ? av - bv : bv - av;
  });

  const trendKey = TREND_KEYS[activeTrend];

  const handleSort = (col) => {
    if (sortCol === col) setSortAsc(a => !a);
    else { setSortCol(col); setSortAsc(false); }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <span style={{ opacity: 0.3 }}>↕</span>;
    return sortAsc ? <span>↑</span> : <span>↓</span>;
  };

  return (
    <div className="page">
      {/* Banner */}
      <div className="cat-banner" style={{ background: grad }}>
        <button className="back-btn" onClick={() => nav('/')}>
          <ArrowLeft size={18} /> Orqaga
        </button>
        <div className="banner-content">
          <h1 className="banner-title">{displayName}</h1>
          <p className="banner-sub">YouTube tahlili va statistikasi</p>
        </div>
        {/* Stat cards overlapping banner bottom */}
        <div className="banner-stats">
          {[
            { tip: `Jami obunachi: ${fmt(cat.subscriberCount)}`, icon: <Users size={18} />, val: fmt(cat.subscriberCount), label: 'Obunachilar' },
            { tip: `Faol kanallar: ${fmt(cat.channelCount)}`, icon: <Video size={18} />, val: fmt(cat.channelCount), label: 'Kanallar' },
            { tip: `Oylik o'rtacha o'sish: +${cat.monthlyGrowth}%`, icon: <TrendingUp size={18} />, val: `+${cat.monthlyGrowth}%`, label: "O'sish" },
            { tip: `Har 1000 ko'rish uchun: $${cat.cpmMin} — $${cat.cpmMax} (taxminiy)`, icon: <DollarSign size={18} />, val: `$${cat.cpmMin}–${cat.cpmMax}`, label: 'CPM ~' },
          ].map((s, i) => (
            <Tooltip key={i} content={s.tip}>
              <motion.div
                className="banner-stat"
                variants={fadeUp} initial="initial" animate="animate"
                transition={{ duration: 0.4, delay: 0.15 + i * 0.08, ease: 'easeOut' }}
                whileHover={{ y: -4, scale: 1.04, transition: { type: 'spring', stiffness: 340, damping: 22 } }}
                whileTap={{ scale: 0.97 }}
              >
                {s.icon}
                <span className="bstat-val">{s.val}</span>
                <span className="bstat-label">{s.label}</span>
              </motion.div>
            </Tooltip>
          ))}
        </div>
      </div>

      {/* Trend Chart */}
      <motion.section
        className="section chart-section"
        style={{ marginTop: 80 }}
        variants={fadeUp} initial="initial" whileInView="animate"
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        {/* Build chart data: use real DB snapshots when >= 7 days exist, else mock */}
        {(() => {
          const useReal = realTrend.isRealData && realTrend.history.length >= 7;
          const chartData = useReal
            ? realTrend.history.map(r => ({
                month: new Date(r.snapshot_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                views: Number(r.view_count),
                subscribers: Number(r.subscriber_count),
                revenueEstimate: Math.round(Number(r.view_count) / 1000 * 5),
              }))
            : trends;

          return (
            <>
              <div className="chart-header">
                <h2 className="section-title">
                  {useReal ? '📈 Real trend' : '6 oylik tendensiya'}
                  {useReal
                    ? <DataBadge isReal={true} label="Live trend" />
                    : <DataBadge isReal={false} label="Taxminiy" />}
                </h2>
                <div className="toggle-group">
                  {Object.keys(TREND_KEYS).map(k => (
                    <button key={k} className={`toggle-btn${activeTrend === k ? ' active' : ''}`}
                      style={activeTrend === k ? { background: color, borderColor: color } : {}}
                      onClick={() => setActiveTrend(k)}>{k}</button>
                  ))}
                </div>
              </div>

              {!useReal && (
                <div style={{
                  padding: '10px 16px', borderRadius: 10, marginBottom: 12,
                  background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.25)',
                  color: '#eab308', fontSize: 13,
                }}>
                  📊 Real ma'lumot to'planmoqda ({realTrend.dataPoints}/7 kun) — hozircha taxminiy trend ko'rsatilmoqda
                </div>
              )}

              <div className="chart-wrap">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} />
                    <YAxis tickFormatter={fmt} tick={{ fontSize: 11, fill: '#94a3b8' }} width={55} />
                    <RTooltip content={<ChartTooltip color={color} />} />
                    <Line type="monotone" dataKey={trendKey} stroke={color}
                      strokeWidth={3} dot={{ r: 4, fill: color }} activeDot={{ r: 6 }}
                      isAnimationActive animationDuration={1500} animationEasing="ease-out" name={activeTrend} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          );
        })()}
      </motion.section>

      {/* Top Channels Table */}
      <motion.section
        className="section"
        variants={fadeUp} initial="initial" whileInView="animate"
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <h2 className="section-title">Top 10 kanal</h2>
        <div className="table-wrap">
          <table className="ch-table">
            <thead>
              <tr>
                <th>№</th>
                <th>Kanal</th>
                <th className="sortable" onClick={() => handleSort('subscriberCount')}>
                  Obunachilar <SortIcon col="subscriberCount" />
                </th>
                <th className="sortable" onClick={() => handleSort('viewCount')}>
                  Ko'rishlar <SortIcon col="viewCount" />
                </th>
                <th className="sortable" onClick={() => handleSort('videoCount')}>
                  Videolar <SortIcon col="videoCount" />
                </th>
                <th>Davlat</th>
                <th className="sortable" onClick={() => handleSort('estimatedMonthlyRevenue')}>
                  Oylik daromad <SortIcon col="estimatedMonthlyRevenue" />
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((ch, i) => (
                <Tooltip key={ch.channelId} content="YouTube kanalini ochish uchun bosing">
                  <tr className="ch-row anim-slideLeft"
                    onClick={() => {
                      console.log('[CategoryDetail] Opening channel:', ch.channelId, ch.title);
                      window.open(`https://www.youtube.com/channel/${ch.channelId}`, '_blank');
                    }}
                    style={{ cursor: 'pointer', animationDelay: `${i * 60}ms` }}>
                    <td className="ch-num">{i + 1}</td>
                    <td>
                      <div className="ch-info">
                        <Avatar url={ch.thumbnailUrl} name={ch.title} color={color} />
                        <span className="ch-name">{ch.title}</span>
                        <ExternalLink size={12} style={{ opacity: 0.4 }} />
                      </div>
                    </td>
                    <td><span className="num-cell">{fmt(ch.subscriberCount)}</span></td>
                    <td><span className="num-cell">{fmt(ch.viewCount)}</span></td>
                    <td>{ch.videoCount?.toLocaleString()}</td>
                    <td>
                      <span className="country-badge">
                        <Globe size={12} /> {ch.country || '—'}
                      </span>
                    </td>
                    <td><span className="revenue-cell">{fmtRevenue(ch.estimatedMonthlyRevenue)}</span></td>
                  </tr>
                </Tooltip>
              ))}
            </tbody>
          </table>
        </div>
      </motion.section>

      {/* Competitors */}
      <motion.section
        className="section"
        variants={fadeUp} initial="initial" whileInView="animate"
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <h2 className="section-title">Raqobatchilar</h2>
        <div className="competitors-grid">
          {competitors.map((ch, i) => {
            const growth = (Math.random() * 10 - 2).toFixed(1);
            const isUp = parseFloat(growth) >= 0;
            return (
              <motion.div
                key={ch.channelId || i}
                className="comp-card"
                style={{ '--accent': color }}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.35, delay: i * 0.07, ease: 'easeOut' }}
                whileHover={{ y: -8, scale: 1.06, transition: { type: 'spring', stiffness: 340, damping: 22 } }}
                whileTap={{ scale: 0.97 }}
                onClick={() => window.open(`https://www.youtube.com/channel/${ch.channelId}`, '_blank')}
              >
                <Avatar url={ch.thumbnailUrl} name={ch.title} color={color} />
                <div className="comp-name">{ch.title}</div>
                <div className="comp-subs">{fmt(ch.subscriberCount)} obunachi</div>
                <div className="comp-views">{fmt(ch.viewCount)} ko'rish</div>
                <span className={`growth-badge ${isUp ? 'up' : 'down'}`} style={{ marginTop: 8 }}>
                  {isUp ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                  {isUp ? '+' : ''}{growth}%
                </span>
              </motion.div>
            );
          })}
        </div>
      </motion.section>

      {/* AI Chat info banner */}
      <motion.section
        className="section"
        variants={fadeUp} initial="initial" whileInView="animate"
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className="rec-box" style={{ '--accent': color }}>
          <div className="rec-content">
            <div style={{ fontSize: 32 }}>🤖</div>
            <div>
              <h3 className="rec-title">Bu yo'nalish uchun AI sizga shaxsiy maslahat beradi</h3>
              <p className="rec-sub">
                {displayName} sohasida kanal ochish, monetizatsiya va o'sish strategiyasi haqida pastdagi AI chat orqali so'rang.
              </p>
            </div>
          </div>
          <div style={{
            background: color, color: '#fff', padding: '12px 22px',
            borderRadius: 12, fontWeight: 700, fontSize: 14, display: 'flex',
            alignItems: 'center', gap: 8,
          }}>
            ✨ Pastda mavjud
          </div>
        </div>
      </motion.section>

      {/* Built-in AI Chat */}
      <AiChat categoryName={cat.name} color={color} displayName={displayName} />
    </div>
  );
}

function SkeletonDetail() {
  return (
    <div className="page">
      <div className="skeleton" style={{ height: 240, borderRadius: 0 }} />
      <div style={{ padding: '0 24px', marginTop: 80 }}>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 40, marginBottom: 16, borderRadius: 8 }} />
        ))}
      </div>
    </div>
  );
}
